/**
 * Times on a card for guests (data/time.json; SpeiseKarte S62, 03.11.2026): weekdays and times of day
 * in every language of the cards, and the short forms a card writes („Mo–Fr 11:30–14:30“). A time is
 * always a range, so no language needs words for „from“ and „until“. Which times a business has and
 * when what applies stay with the app.
 */
import data from "../data/time.json" with { type: "json" };
import type { Lang } from "./langs.ts";

export interface TimeWords {
  /** Monday first, as cards write them short: „Mo“, „Mon“. */
  days: readonly string[];
  /** All seven days: „täglich“. */
  daily: string;
  today: string;
  tomorrow: string;
  /** The line on the net on a day the business is closed. */
  closedToday: string;
  /** Between hours and minutes: „:“, French „h“. */
  clock: string;
}

export const TIME_WORDS: Readonly<Record<Lang | "de", Readonly<TimeWords>>> = data.words;

/** A time of day from minutes after midnight: 690 → „11:30“, French „11h30“; 1440 → „24:00“. */
export function clockText(minutes: number, lang: Lang | "de" = "de"): string {
  const m = Math.max(0, Math.min(1440, Math.round(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}${TIME_WORDS[lang].clock}${String(m % 60).padStart(2, "0")}`;
}

/**
 * Weekdays (0 = Monday … 6 = Sunday) as a card writes them: runs of three or more with a dash
 * („Mo–Fr“), others by comma („Sa, So“, „Mo, Mi, Fr“); all seven „täglich“; none „“.
 */
export function daysText(days: readonly number[], lang: Lang | "de" = "de"): string {
  const on = [...new Set(days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
  const w = TIME_WORDS[lang];
  if (on.length === 7) return w.daily;
  const parts: string[] = [];
  for (let i = 0; i < on.length; ) {
    let j = i;
    while (j + 1 < on.length && on[j + 1] === on[j]! + 1) j++;
    if (j - i >= 2) parts.push(`${w.days[on[i]!]}–${w.days[on[j]!]}`);
    else for (let k = i; k <= j; k++) parts.push(w.days[on[k]!]!);
    i = j + 1;
  }
  return parts.join(", ");
}

/** A range of time: „11:30–14:30“. */
export const rangeText = (from: number, to: number, lang: Lang | "de" = "de") => `${clockText(from, lang)}–${clockText(to, lang)}`;
